"use server";

import { headers } from "next/headers";
import { resolveTenantSlug } from "@/lib/tenant-slug";

// Self-service password reset (Task 12, P5). Same public, pre-auth idiom as
// the rest of this file family (auth-actions.ts, email-change-actions.ts):
// a plain server-to-server POST to the backend container — no auth header,
// the caller is logged out by definition — wrapped in try/catch so a
// network-level failure degrades to a typed result instead of throwing
// across the server-action boundary.
const BASE = process.env.BACKEND_INTERNAL_URL ?? "http://erp-backend:4000";

export type ResetPasswordStatus = "ok" | "expired" | "invalid" | "error";

/**
 * PUBLIC: kick off a reset. Anti-enumeration lives entirely on the backend
 * (PasswordResetService.requestReset never throws and never reveals whether
 * the tenant/e-mail/account exists — every failure mode answers with the
 * same `{ok:true}` as the happy path, @HttpCode(200) always). The only way
 * this ever reports `ok:false` is the SSR container failing to reach the
 * backend at all — the calling page must render the SAME confirmation copy
 * regardless of this result; never branch UI text on it.
 *
 * `tenantSlug` is the raw "Firma" field value (may be empty) and is resolved
 * through the same host-subdomain-first precedence loginAction uses, so a
 * request made from a tenant subdomain targets that tenant even when the
 * field is left blank.
 */
export async function requestPasswordReset(
  email: string,
  tenantSlug: string,
): Promise<{ ok: boolean }> {
  const host = (await headers()).get("host");
  const resolvedSlug = resolveTenantSlug(host, tenantSlug);
  try {
    const res = await fetch(`${BASE}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, tenantSlug: resolvedSlug }),
      cache: "no-store",
    });
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

/**
 * PUBLIC: token-consuming reset — the link token mailed by
 * requestPasswordReset is the only credential. Mirrors
 * confirmEmailChangeAction's status mapping: 410 means the token was valid
 * but has since expired; any other non-ok status reads as "invalid" (bad,
 * already-used or deleted-account token — the backend does not distinguish
 * these and neither does the UI). Password shape (length/match) is
 * validated client-side before this is ever called.
 */
export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<ResetPasswordStatus> {
  try {
    const res = await fetch(`${BASE}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
      cache: "no-store",
    });
    if (res.ok) return "ok";
    if (res.status === 410) return "expired";
    return "invalid";
  } catch {
    return "error";
  }
}
